import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isForbidden, schedule } from '@cwos/api-client';
import type { schedule as SCH } from '@cwos/api-client';
import { Avatar, Pill } from '@cwos/ui-admin';
import { photos } from '@cwos/api-client';
import { usePermissions } from '../permissions.js';
import {
  CAPACITY_READING_TEXT,
  DATED_STANDING_READING,
  OFFER_STANDING_TEXT,
  canAcceptCandidate,
  canIncludeCandidate,
  candidateStandingText,
  inclusionStandingText,
  offerStanding,
  authorizationStanding,
  ceilingCheckReading,
  dateWindowReading,
  disagreementLabel,
  eligibilityStanding,
  exclusionReading,
  heldCandidates,
  heldInServerAudience,
  formatIsoDate,
  hoursAndMinutes,
  localDateIn,
  messageOf,
  offerReadiness,
  offerableAudience,
  type EligibilityStanding,
} from './coverageLogic.js';

/**
 * WHO CAN COVER THIS SHIFT, AND IS THE CLIENT AUTHORIZED TO RECEIVE IT.
 *
 * The coverage engine, the authorization ceiling and the eligibility domain
 * have all been complete and invisible: `GET /availability/coverage/*`,
 * `POST /availability/coverage/offers` and
 * `GET /schedule/clients/:id/service-level` had no caller anywhere in the
 * product. A scheduler answered "can this person take this work" by asking
 * people.
 *
 * ---------------------------------------------------------------------------
 * WHY THE HOLD IS THE CENTRE OF THIS PANEL
 * ---------------------------------------------------------------------------
 * A worker on an active eligibility hold used to be offerable: the backend
 * hold existed, `eligibilityGuard` refused them at the door, and nothing
 * surfaced it at the moment of offering. The engine now consults eligibility,
 * and this panel renders that answer as the FIRST thing about a candidate —
 * before the score, before the availability — because an offer that will be
 * refused is worse than no offer. It wastes a scheduler's time, and to the
 * worker it looks like the system changed its mind.
 *
 * The panel also refuses independently. `offerableAudience` is computed from
 * `considered`, and if the server's `audience` disagrees — if it ever contains
 * somebody the criteria say is held — the panel SAYS SO and offers the smaller
 * list. See `coverageLogic.ts` §1.
 *
 * ---------------------------------------------------------------------------
 * THE FOUR STATES, EXPLICITLY
 * ---------------------------------------------------------------------------
 * Loading, empty, error and REFUSED are four different sentences here, and
 * three of them are dangerous as the fourth. "Nobody can cover this shift" and
 * "you may not see who can cover this shift" lead a scheduler to opposite
 * actions, and this panel is one of the places where the empty answer is most
 * ambiguous. `listPhase` is not used because this is not a list panel — the
 * coverage read is one document with several sections — so the states are
 * named here and each is written out.
 *
 * ---------------------------------------------------------------------------
 * WHAT IT WILL NOT DO
 * ---------------------------------------------------------------------------
 * Nothing here assigns anybody. The engine ranks and explains; a person
 * chooses. The only write is creating the OFFER, which is the act the API
 * exposes, and it is disabled when nobody may be offered rather than sending a
 * request the server will refuse with a 409.
 */

/**
 * `service:manage`, NOT `service:read`, AND THAT IS THE SERVER'S RULE.
 *
 * PH-AUTHZ-003: `GET /schedule/clients/:clientId/service-level` had no
 * per-client narrowing at all and charged `service:read`, which is seeded to
 * the `worker` role — so a uuid alone bought one client's payer, payer
 * reference and authorized ceiling. Reading a ceiling is now a management act
 * on the API, and this gate mirrors it so the tile is ABSENT for a principal
 * who may not ask, instead of rendering and then showing a refusal.
 *
 * Nothing is lost here: this query is already downstream of `coverage.data`,
 * which needs `schedule:manage` — held by org_admin and supervisor, and both
 * of those hold `service:manage`.
 */
const CEILING_READ = 'service:manage';
const COVERAGE_MANAGE = 'schedule:manage';

function standingTone(standing: EligibilityStanding): 'good' | 'urgent' | 'unsure' {
  switch (standing) {
    case 'clear':
      return 'good';
    case 'held':
      return 'urgent';
    default:
      return 'unsure';
  }
}

const STANDING_LABEL: Record<EligibilityStanding, string> = {
  clear: 'eligible',
  held: 'on hold',
  unchecked: 'eligibility not checked',
  absent: 'eligibility not checked',
};

export function CoveragePanel({
  shiftId,
  /** Injected so the dated comparisons are testable without a fake clock. */
  now = new Date(),
}: {
  shiftId: string;
  now?: Date;
}) {
  const qc = useQueryClient();
  const { has } = usePermissions();
  const canManageCoverage = has(COVERAGE_MANAGE);
  const canReadCeiling = has(CEILING_READ);
  const [offerError, setOfferError] = useState<string | null>(null);
  const [offered, setOffered] = useState<SCH.OfferView | null>(null);

  const coverage = useQuery({
    queryKey: ['coverage', 'recommendations', shiftId],
    queryFn: () => schedule.coverageRecommendations({ shiftId }),
    enabled: canManageCoverage,
  });

  const clientId = coverage.data?.need.clientId ?? null;

  const serviceLevel = useQuery({
    queryKey: ['coverage', 'service-level', clientId, coverage.data?.need.startsAt],
    queryFn: () => schedule.clientServiceLevel(clientId!, { anchor: coverage.data!.need.startsAt }),
    enabled: canReadCeiling && clientId !== null && coverage.data !== undefined,
  });

  const makeOffer = useMutation({
    mutationFn: (mode: 'manager_led' | 'open_shift') =>
      schedule.createCoverageOffer({ shiftId, mode }),
    onSuccess: (offer) => {
      setOffered(offer);
      setOfferError(null);
      void qc.invalidateQueries({ queryKey: ['coverage', 'recommendations', shiftId] });
    },
    onError: (err: unknown) => setOfferError(messageOf(err)),
  });

  // A key the reader does not hold means the section is ABSENT, not a ghost —
  // the drawer's own rule. A refusal is different and is rendered below.
  if (!canManageCoverage) return null;

  return (
    <section className="drawer-section" aria-label="Coverage">
      <h3 className="drawer-label">Coverage</h3>

      {coverage.isError ? (
        isForbidden(coverage.error) ? (
          <p className="rn-muted" style={{ margin: 0 }}>
            Who could cover this shift is not shown to you. This is an answer, not an empty list —
            do not read it as “nobody is available”.
          </p>
        ) : (
          <p className="rn-muted" style={{ margin: 0 }}>
            We couldn’t work out who could cover this shift just now. Do not read this as “nobody is
            available”.{' '}
            <button type="button" className="rn-link" onClick={() => void coverage.refetch()}>
              Try again
            </button>
          </p>
        )
      ) : coverage.data === undefined ? (
        <p className="rn-muted" style={{ margin: 0 }}>
          Working out who could cover this…
        </p>
      ) : (
        <CoverageBody
          coverage={coverage.data}
          now={now}
          canReadCeiling={canReadCeiling}
          serviceLevel={serviceLevel}
          offered={offered}
          offerError={offerError}
          offerPending={makeOffer.isPending}
          onOffer={(mode) => makeOffer.mutate(mode)}
        />
      )}

      <OffersOnThisShift shiftId={shiftId} />
    </section>
  );
}

/**
 * WHAT WAS ALREADY OFFERED ON THIS SHIFT, AND WHO HAS ANSWERED.
 *
 * The panel above makes an offer and, until this existed, that was the last
 * anybody saw of it. `POST /availability/coverage/offers` returns an `OfferView`
 * once; nothing read one back. So a scheduler could not tell whether the four
 * people they asked yesterday had answered, could not accept on somebody's
 * behalf — accepting needs an offer id nobody held — and could not tell an
 * offer nobody answered from one everybody declined. Those lead to opposite
 * next actions: chase, or widen.
 *
 * IT IS RENDERED BELOW THE RECOMMENDATIONS ON PURPOSE. The question "who could
 * cover this" comes before "what did we already do about it" only when nothing
 * has been done; once an offer is out, the outstanding answers are the live
 * thing. Ordering it the other way would put a list of hypotheticals above a
 * question a real person has been asked.
 *
 * THE FOUR STATES, KEPT APART, as everywhere else in this drawer: a refusal, a
 * failed read, a real measured zero (no offer has ever been made on this shift)
 * and the list. The refusal cannot actually be reached today — `CoveragePanel`
 * has already returned `null` without `schedule:manage`, and this route charges
 * the same key — and it is written anyway, because the day the panel's own gate
 * changes is the day this becomes the only thing standing between a scheduler
 * and an unexplained empty list.
 */
function OffersOnThisShift({ shiftId }: { shiftId: string }) {
  const qc = useQueryClient();
  const [acceptError, setAcceptError] = useState<string | null>(null);
  const [accepting, setAccepting] = useState<string | null>(null);
  const [includeError, setIncludeError] = useState<string | null>(null);
  const [including, setIncluding] = useState<string | null>(null);

  const offers = useQuery({
    queryKey: ['coverage', 'offers', shiftId],
    queryFn: () => schedule.coverageOffers({ shiftId }),
  });

  const accept = useMutation({
    mutationFn: ({ offerId, workerId }: { offerId: string; workerId: string }) =>
      schedule.acceptCoverageOffer(offerId, workerId),
    onMutate: ({ workerId }) => {
      setAcceptError(null);
      setAccepting(workerId);
    },
    onSuccess: () => {
      // BOTH READS, because both moved. The offer is filled and the shift now
      // names a different worker, and a drawer showing the new offer state
      // beside the old roster would be two answers about one act.
      void qc.invalidateQueries({ queryKey: ['coverage', 'offers', shiftId] });
      void qc.invalidateQueries({ queryKey: ['coverage', 'recommendations', shiftId] });
      void qc.invalidateQueries({ queryKey: ['shifts'] });
    },
    onError: (err: unknown) => setAcceptError(messageOf(err)),
    onSettled: () => setAccepting(null),
  });

  /**
   * "Let <name> answer this" — the founder's third worker-reach ruling of
   * 2026-09-09, as an act somebody can perform.
   *
   * Only the OFFER read is invalidated. Nothing else moved: no shift changed
   * hands, no candidacy was answered, and the recommendation list is about who
   * COULD cover, which this does not touch. Invalidating `['shifts']` here would
   * tell the rest of the screen that the roster changed when it did not.
   */
  const include = useMutation({
    mutationFn: ({ offerId, workerId }: { offerId: string; workerId: string }) =>
      schedule.includeCandidateInWorkerOffers(offerId, workerId),
    onMutate: ({ workerId }) => {
      setIncludeError(null);
      setIncluding(workerId);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['coverage', 'offers', shiftId] });
    },
    onError: (err: unknown) => setIncludeError(messageOf(err)),
    onSettled: () => setIncluding(null),
  });

  return (
    <div className="drawer-rows" style={{ marginTop: 'var(--space-12)' }}>
      <h4 className="drawer-label" style={{ margin: 0 }}>
        Offers on this shift
      </h4>

      {offers.isError ? (
        <p className="rn-muted" style={{ margin: 0 }}>
          {isForbidden(offers.error)
            ? 'The offers made on this shift are not shown to you. Do not read that as “none were made”.'
            : 'The offers on this shift couldn’t be read just now. Do not read that as “none were made”.'}{' '}
          {isForbidden(offers.error) ? null : (
            <button type="button" className="rn-link" onClick={() => void offers.refetch()}>
              Try again
            </button>
          )}
        </p>
      ) : offers.data === undefined ? (
        <p className="rn-muted" style={{ margin: 0 }}>
          Reading the offers…
        </p>
      ) : offers.data.length === 0 ? (
        <p className="rn-muted" style={{ margin: 0 }}>
          No offer has been made on this shift. This is a real answer about the shift, not a failed
          read.
        </p>
      ) : (
        <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
          {offers.data.map((offer) => {
            const standing = offerStanding(offer);
            return (
              <li key={offer.id} className="drawer-rows" style={{ padding: 'var(--space-8) 0' }}>
                <div className="drawer-row">
                  <span>
                    {offer.mode === 'open_shift' ? 'Open shift' : 'Shortlist'} · offered to{' '}
                    {offer.audienceSize} of {offer.eligiblePoolSize} considered
                  </span>
                  <Pill
                    variant={
                      standing === 'open'
                        ? 'unsure'
                        : standing === 'filled'
                          ? 'good'
                          : standing === 'unknown'
                            ? 'unsure'
                            : 'urgent'
                    }
                    dot
                  >
                    {standing}
                  </Pill>
                </div>
                <p className="rn-muted" style={{ margin: 0 }}>
                  {OFFER_STANDING_TEXT[standing]}
                </p>
                <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                  {offer.candidates.map((candidate) => (
                    <li key={candidate.workerId} className="drawer-row">
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 'var(--space-8)',
                        }}
                      >
                        <Avatar
                          name={candidate.displayName}
                          src={photos.personPhotoSrc({
                            userId: candidate.userId,
                            photoUpdatedAt: candidate.photoUpdatedAt,
                          })}
                          size={24}
                        />
                        {candidate.displayName}
                        <span className="rn-muted">{candidateStandingText(candidate.status)}</span>
                        {inclusionStandingText(offer, candidate) !== null ? (
                          <span className="rn-muted">
                            · {inclusionStandingText(offer, candidate)}
                          </span>
                        ) : null}
                      </span>
                      {canIncludeCandidate(offer, candidate) ? (
                        <button
                          type="button"
                          className="rn-btn-quiet"
                          disabled={including !== null}
                          onClick={() =>
                            include.mutate({ offerId: offer.id, workerId: candidate.workerId })
                          }
                        >
                          {including === candidate.workerId
                            ? 'Asking…'
                            : `Let ${candidate.displayName} answer`}
                        </button>
                      ) : null}
                      {canAcceptCandidate(offer, candidate) ? (
                        <button
                          type="button"
                          className="rn-btn-quiet"
                          disabled={accepting !== null}
                          onClick={() =>
                            accept.mutate({ offerId: offer.id, workerId: candidate.workerId })
                          }
                        >
                          {accepting === candidate.workerId
                            ? 'Accepting…'
                            : `Accept for ${candidate.displayName}`}
                        </button>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </li>
            );
          })}
        </ul>
      )}

      {acceptError !== null ? (
        <p role="status" className="rn-muted" style={{ color: 'var(--color-urgent-deep)' }}>
          {acceptError} Nothing was changed.
        </p>
      ) : null}

      {includeError !== null ? (
        <p role="status" className="rn-muted" style={{ color: 'var(--color-urgent-deep)' }}>
          {includeError} Nothing was changed, and nobody was told anything.
        </p>
      ) : null}

      {/*
        SAID OUT LOUD, because "Let <name> answer" is a smaller act than it
        looks and a larger one than it sounds. It does NOT give them the shift
        and it does not narrow the shortlist: everybody else on it stays exactly
        where they were, and a roster holder can still accept for any of them.
        What it does is make one person able to take the shift themselves — and
        it tells them so, with a notification that names no client, no site and
        no program, because push text reaches a lock screen.
      */}
      <p className="rn-muted" style={{ margin: 0 }}>
        “Let someone answer” puts one person on the worker’s side of this offer. It hands over no
        shift, tells nobody else, and can be undone only by the shift being filled or the offer
        closing.
      </p>

      {/*
        SAID OUT LOUD, because the button above says "Accept for <name>" and that
        is not the same act as the person taking it themselves. An acceptance
        entered here is recorded as the roster holder's decision
        (`schedule.coverage_offer_filled`); a worker taking an open shift on
        their own phone writes `schedule.coverage_offer_claimed`. An organization
        reviewing its scheduling has to be able to tell those apart, and the
        audit trail can.
      */}
      <p className="rn-muted" style={{ margin: 0 }}>
        Accepting here is recorded as your decision, not as the worker’s. On an open shift they can
        also take it themselves.
      </p>
    </div>
  );
}

function CoverageBody({
  coverage,
  now,
  canReadCeiling,
  serviceLevel,
  offered,
  offerError,
  offerPending,
  onOffer,
}: {
  coverage: SCH.CoverageResult;
  now: Date;
  canReadCeiling: boolean;
  serviceLevel: {
    data: { serviceLevel: SCH.ServiceLevelReconciliation; timezone: string } | undefined;
    isError: boolean;
    error: unknown;
  };
  offered: SCH.OfferView | null;
  offerError: string | null;
  offerPending: boolean;
  onOffer: (mode: 'manager_led' | 'open_shift') => void;
}) {
  const readiness = offerReadiness(coverage);
  const offerable = offerableAudience(coverage);
  const held = heldCandidates(coverage);
  const disagreement = heldInServerAudience(coverage);
  const today = localDateIn(coverage.need.timezone, now);

  return (
    <>
      {/* ------------------------------------------------------------------
          IS THE CLIENT AUTHORIZED TO RECEIVE THIS WORK.
          The ceiling is dated. `status: 'active'` is not a claim about today.
         ------------------------------------------------------------------ */}
      {coverage.need.clientId === null ? null : !canReadCeiling ? null : (
        <div className="drawer-rows" style={{ marginBottom: 'var(--space-12)' }}>
          <h4 className="drawer-label" style={{ margin: 0 }}>
            Authorized to receive this
          </h4>
          {serviceLevel.isError ? (
            <p className="rn-muted" style={{ margin: 0 }}>
              {isForbidden(serviceLevel.error)
                ? 'What this client is authorized for is not shown to you. Do not read that as “no ceiling”.'
                : 'The authorization ceiling couldn’t be read just now. Do not read that as “no ceiling”.'}
            </p>
          ) : serviceLevel.data === undefined ? (
            <p className="rn-muted" style={{ margin: 0 }}>
              Reading the authorization…
            </p>
          ) : serviceLevel.data.serviceLevel.ceilings.length === 0 ? (
            <p className="rn-muted" style={{ margin: 0 }}>
              No authorization has been recorded for this client. Nothing here limits the booking —
              and nothing here supports billing it either.
            </p>
          ) : (
            <>
              <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                {serviceLevel.data.serviceLevel.ceilings.map((auth) => {
                  const standing = authorizationStanding(auth, today);
                  const window = dateWindowReading(auth.startsOn, auth.endsOn);
                  const check = serviceLevel.data?.serviceLevel.ceilingChecks.find(
                    (item) => item.authorizationId === auth.id,
                  );
                  const checkReading = check === undefined ? null : ceilingCheckReading(check);
                  return (
                    <li key={auth.id}>
                      <div className="drawer-row">
                        <span>
                          {auth.authorizingEntity} · {auth.maxUnits} {auth.unit} per {auth.period}
                          {window === null ? null : <span className="rn-muted"> · {window}</span>}
                        </span>
                        <Pill
                          variant={
                            standing === 'in_force'
                              ? 'good'
                              : standing === 'revoked' || standing === 'ended'
                                ? 'urgent'
                                : 'unsure'
                          }
                          dot
                        >
                          {DATED_STANDING_READING[standing]}
                        </Pill>
                      </div>
                      {checkReading === null ? null : (
                        <div
                          className="rn-muted"
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--space-8)',
                            margin: 'var(--space-4) 0 var(--space-8)',
                          }}
                        >
                          {checkReading.text}
                          <Pill variant={checkReading.pill.variant} dot>
                            {checkReading.pill.label}
                          </Pill>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </div>
      )}

      {serviceLevel.data ? (
        <>
          <p className="rn-muted" style={{ margin: 0 }}>
            On the calendar in this window: {serviceLevel.data.serviceLevel.scheduledMinutes} min.
          </p>
          {serviceLevel.data.serviceLevel.expectations.length > 0 ? (
            <>
              <h4 className="drawer-label">Expected to be delivered</h4>
              <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                {serviceLevel.data.serviceLevel.expectations.map((expectation) => {
                  const expectationWindow = dateWindowReading(
                    expectation.startsOn,
                    expectation.endsOn,
                  );
                  return (
                    <li key={expectation.id} className="drawer-row">
                      <span>
                        {expectation.source}: {expectation.expectedUnitsMin}
                        {expectation.expectedUnitsMax === null
                          ? ''
                          : `–${expectation.expectedUnitsMax}`}{' '}
                        {expectation.unit} per {expectation.period}
                        {expectationWindow === null ? null : (
                          <span className="rn-muted"> · {expectationWindow}</span>
                        )}
                        {expectation.note === null ? null : (
                          <span className="rn-muted"> · {expectation.note}</span>
                        )}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </>
          ) : null}
          {serviceLevel.data.serviceLevel.disagreements.length > 0 ? (
            <>
              <h4 className="drawer-label">Where the sources disagree</h4>
              <ul role="list" style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                {serviceLevel.data.serviceLevel.disagreements.map((item, index) => {
                  const label = disagreementLabel(item.kind);
                  return (
                    <li key={`${item.kind}-${index}`} className="drawer-row">
                      {label === null ? item.message : `${label}: ${item.message}`}
                    </li>
                  );
                })}
              </ul>
            </>
          ) : null}
        </>
      ) : null}

      {/* ------------------------------------------------------------------
          THE HOLD, AT THE MOMENT OF OFFERING.
         ------------------------------------------------------------------ */}
      {coverage.eligibilityUnchecked && coverage.considered.length > 0 ? (
        <p role="status" className="rn-muted" style={{ margin: '0 0 var(--space-8)' }}>
          Nobody on this list has been checked against eligibility holds. That is not the same as
          everybody being clear — a held worker offered a shift will be refused when they try to
          take it.
        </p>
      ) : null}

      {disagreement.length > 0 ? (
        <p
          role="status"
          className="rn-muted"
          style={{ margin: '0 0 var(--space-8)', color: 'var(--color-urgent-deep)' }}
        >
          {disagreement.length === 1
            ? '1 person in the offer list is on an eligibility hold and has been removed here.'
            : `${disagreement.length} people in the offer list are on eligibility holds and have been removed here.`}{' '}
          They would be refused the shift.
        </p>
      ) : null}

      {coverage.considered.length === 0 ? (
        <p className="rn-muted" style={{ margin: 0 }}>
          Nobody was considered for this shift. There is no pool to draw from — this is a real
          answer about your roster, not a failed read.
        </p>
      ) : (
        <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
          {coverage.considered.map((rec) => {
            const standing = eligibilityStanding(rec);
            const why = exclusionReading(rec);
            return (
              <li
                key={rec.workerId}
                className="drawer-rows"
                style={{ padding: 'var(--space-8) 0' }}
              >
                <div className="drawer-row">
                  <span
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-8)' }}
                  >
                    <Avatar
                      name={rec.displayName}
                      src={photos.personPhotoSrc({
                        userId: rec.userId,
                        photoUpdatedAt: rec.photoUpdatedAt,
                      })}
                      size={24}
                    />
                    {rec.displayName}
                  </span>
                  <Pill variant={standingTone(standing)} dot>
                    {STANDING_LABEL[standing]}
                  </Pill>
                </div>
                <p className="rn-muted" style={{ margin: 0 }}>
                  {why ?? rec.explanation}
                </p>
              </li>
            );
          })}
        </ul>
      )}

      {/* ------------------------------------------------------------------
          THE OFFER.
         ------------------------------------------------------------------ */}
      <div className="drawer-actions">
        {readiness.kind === 'ready' || readiness.kind === 'unverified' ? (
          <>
            <button
              type="button"
              className="rn-btn-quiet"
              disabled={offerPending}
              onClick={() => onOffer('manager_led')}
            >
              {offerPending
                ? 'Offering…'
                : `Offer to ${offerable.length > 0 ? offerable.length : readiness.count} ${
                    (offerable.length > 0 ? offerable.length : readiness.count) === 1
                      ? 'person'
                      : 'people'
                  }`}
            </button>
            {readiness.kind === 'unverified' ? (
              <span className="rn-muted">Eligibility was not checked for anybody here.</span>
            ) : null}
          </>
        ) : readiness.kind === 'blocked' ? (
          <p role="status" className="rn-muted" style={{ margin: 0 }}>
            {held.length === 0
              ? 'Everybody considered was excluded by the coverage check, so there is nobody to offer this to.'
              : held.length === coverage.considered.length
                ? 'Everybody considered is on an eligibility hold, so there is nobody to offer this to. Lifting a hold is a Compliance decision, not a scheduling one.'
                : `${held.length} of ${coverage.considered.length} considered are on eligibility holds, and the rest were excluded for other reasons. There is nobody to offer this to.`}
          </p>
        ) : null}
      </div>

      {offerError !== null ? (
        <p role="status" className="rn-muted" style={{ color: 'var(--color-urgent-deep)' }}>
          {offerError} No offer was made.
        </p>
      ) : null}

      {offered !== null ? (
        <p role="status" className="rn-muted">
          Offered to {offered.audienceSize} {offered.audienceSize === 1 ? 'person' : 'people'} of{' '}
          {offered.eligiblePoolSize} considered.
        </p>
      ) : null}

      {/*
        WHEN THIS ANSWER WAS COMPUTED, AND OVER WHAT.

        Capacity is a WEEKLY quantity, so a candidate's remaining hours were
        measured over the organization's week containing the shift — not over
        the shift, and not over the reader's week. Saying so is what stops a
        scheduler carrying this list into a different week and believing it.
        `formatIsoDate` is fed the org-local calendar date of the window start,
        never the raw instant, because the window bounds are UTC instants cut on
        an org-local boundary and formatting them in the device zone prints the
        previous day for any reader west of the organization.
      */}
      <p className="rn-muted" style={{ margin: 'var(--space-8) 0 0' }}>
        Measured over the week beginning{' '}
        {formatIsoDate(localDateIn(coverage.window.timezone, new Date(coverage.window.from)))} in{' '}
        {coverage.window.timezone}. Availability changes; re-open this before acting on it later in
        the day.
      </p>
    </>
  );
}

/** Exported for the availability panel, which renders the same three zeroes. */
export function capacitySentence(capacity: SCH.CapacityView): string {
  const reading =
    capacity.declaredNothing === true
      ? 'undeclared'
      : capacity.remainingMinutes > 0
        ? 'open'
        : 'full';
  const detail =
    reading === 'undeclared' ? '' : ` ${hoursAndMinutes(capacity.remainingMinutes)} remaining.`;
  return `${CAPACITY_READING_TEXT[reading]}${detail}`;
}
